<!-- /src/components/Course/SessionList.vue -->
<template>
  <div class="session-list">
    <header
      class="session-list__overview"
      @click="toggleSection"
      :class="{
        'session-list__overview__success': sectionIsComplete,
        'session-list__overview__closed': hideSessions,
      }"
    >
      <h2 v-if="section.title">{{ section.title }}</h2>
      <h2 v-else>Course Content</h2>
      <div v-if="isLoading">Loading ...</div>
      <div v-else-if="percentViewed >= 100" class="button__icon"> 
        <i class="icon fa fa-check-circle"></i>
      </div>
      <div v-else class="percentage-indicator">{{ percentViewed }}% complete</div>
    </header>
    <div class="body-content" v-show="!hideSessions" v-html="section.description"></div>
    <transition name="slide-fade">
      <!-- Use processedSessions here -->
      <ul v-show="!hideSessions" class="session-list__items">
        <li
          class="session-list__items__item"
          v-for="(session, index) in processedSessions" 
          :key="`session-list_${session.id || index}`"
        >
          <PostListItem
            v-if="session.id"
            :post="session"
            :next="nextSession"
            @updateCourse="handleUpdateCourse" 
            @trackCurrentTime="handleTrackCurrentTime"
          />
        </li>
      </ul>
    </transition>
  </div>
</template>

<script>
import PostListItem from '@/components/Course/SessionItem'
// Use native JS methods instead of lodash when possible to reduce bundle size
// import _isUndefined from 'lodash/isUndefined'
// import _each from 'lodash/each'
// import _findIndex from 'lodash/findIndex'
import { checkNested } from '@/components/common/utils/helpers.js'
import secondConversions from '@/components/common/utils/filters/secondConversions'
import cloneDeep from 'lodash/cloneDeep' // Keep for deep copies if needed
// import remove from 'lodash/remove' // Not used currently
import Loader from '@/components/common/Loaders/Loader'

export default {
  data() {
    return {
      sessionsCompleted: [],
      // totalTime and timeCompleted can be computed properties
      // percentViewed can be a computed property
      sectionIsComplete: false,
      hideSessions: true,
      sessionInteractions: [], // This will be the mutable source for interaction data
      sectionSessionsList: [], // Not used in provided code?
      currentSession: false,
      nextSession: {},
      isLoading: true,
      // manipulatedSessions is now a computed property, remove from data
      // manipulatedSessions: []
    }
  },
  props: {
    id: { type: Number, required: true },
    type: { type: String, required: true },
    sectionKey: { type: String, required: true },
    section: { type: Object, required: true },
    nextSection: { type: Object },
    courseInteractions: { type: [Object, Boolean], required: true },
  },
  computed: {
    children() {
      return this.section.children
    },
    child_objects() {
      return this.section.child_objects
    },
    // This computed property combines base session data with interaction data
    // and also the child_objects data. This is now the single source for the list.
    processedSessions() {
      const allSessionsFromStore = this.$store.getters['posts/filterAllSessionsByProp'](this.children);
      
      // // Step 1: Combine sessions from store with sessionInteractions
      // const sessionsWithInteractions = allSessionsFromStore.map(session => {
      //   const action = this.sessionInteractions.find(act => act && act.id === session.id);
      //   return {
      //     ...session, // Start with session data
      //     ...(action || {}) // Overlay interaction data if found
      //   };
      // });

      // // Step 2: Combine with child_objects (which seems to augment session data)
      // const finalSessions = sessionsWithInteractions.map(session => {
      //   const obj = this.child_objects.find(co => {
      //     return session.id === co.id});
      //   // console.log(session)
      //   // console.log(obj)
      //   return {
      //     ...(obj || {}), // Overlay child_object data if found
      //     ...session, // Start with combined session & interaction data
      //   };
      // }).filter(session => Object.keys(session).length !== 0); // Filter out empty objects
      // console.log(finalSessions)

            // Step 2: Combine with child_objects (which seems to augment session data)
      const augmentedSessions = allSessionsFromStore.map(session => {
        const obj = this.child_objects.find(co => {
          return session.id === co.session_id});
        // console.log(session.id)
        // console.log({obj})
        // console.log({session})
        // console.log('-----')
        return {
          ...session, // Start with combined session & interaction data
          ...(obj || {}), // Overlay child_object data if found
        };
      }); // Filter out empty objects
      
            // Step 1: Combine sessions from store with sessionInteractions
      const finalSessions = augmentedSessions.map(session => {
        const action = this.sessionInteractions.find(act => act && act.id === session.id);
        return {
          ...session, // Start with session data
          ...(action || {}) // Overlay interaction data if found
        };
      }).filter(session => Object.keys(session).length !== 0);

      // After computing, update related status (like current/next session, completion)
      // This is less ideal than watchers for triggering side effects, but necessary
      // if these depend directly on the *final* list content.
      // A better pattern for `currentSession` and `nextSession` is below in methods/watchers.
      
      // Let's call these side-effect methods here, or ensure they are reactive to `processedSessions`
      // For instance, a watcher on `processedSessions` could trigger `getSessionElements`
      
      return finalSessions;
    },

    // New computed property for total duration
    totalTime() {
      return this.processedSessions.reduce((sum, session) => {
        const acfDuration = Array.isArray(session.duration) && session.duration[0] && typeof session.duration[0].total_duration !== 'undefined'
          ? parseInt(session.duration[0].total_duration)
          : 0;
        // Fallback to mediaDuration stored in interaction (set after first play)
        const duration = acfDuration || session.mediaDuration || 0;
        return sum + duration;
      }, 0);
    },

    // New computed property for time completed
    timeCompleted() {
      return this.processedSessions.reduce((sum, session) => {
        const dateCompleted = session && session.dateCompleted;
        const acfDuration = Array.isArray(session.duration) && session.duration[0] && typeof session.duration[0].total_duration !== 'undefined'
          ? parseInt(session.duration[0].total_duration)
          : 0;
        // Fallback to mediaDuration stored in interaction (set after first play)
        const sessionDuration = acfDuration || session.mediaDuration || 0;
        
        // If dateCompleted, use full duration.
        // Else if timestamp exists, use timestamp.
        // Else 0.
        const completedTime = dateCompleted
          ? sessionDuration
          : (session.timestamp || 0);
          
        return sum + completedTime;
      }, 0);
    },

    // New computed property for percent viewed
    percentViewed() {
      if (this.totalTime === 0) return 0;
      return Math.floor((this.timeCompleted / this.totalTime) * 100);
    },

    dateStarted() {
      const { currentInteraction } = this
      return currentInteraction &&
        typeof currentInteraction.dateStarted != 'undefined'
        ? new Date(currentInteraction.dateStarted).getTime()
        : new Date().getTime()
    },
    interactionSecSet() {
      const { id, type, sectionKey } = this
      return {
        id,
        type,
        sectionKey,
      }
    },
  },
  watch: {
    // Watch for changes in the route to update current session
    $route: {
      handler() {
        this.getSessionElements('route')
      },
      immediate: true // Run on component mount
    },
    // Watch processedSessions to update status and hide/show sections
    processedSessions: {
      handler(newSessions) {
        if (newSessions.length > 0) {

          // Trigger status updates once processedSessions are ready
          this.updateSectionCompletionStatus();
          this.isLoading = false;
  
          // If you need to re-evaluate currentSession/nextSession based on processedSessions
          // it's better to trigger the method that handles it.
          // However, `getSessionElements` is already watching $route, which might be sufficient.
          // If not, explicitly call it here IF `findCurrentSession` relies on `processedSessions` being ready.
          this.getSessionElements(); // Uncomment if logic demands it after initial data load
        }
      },
      immediate: true, // Run on component mount right after initial data fetch
    },
    // Watch percentViewed to update sectionIsComplete
    percentViewed(newVal) {
      if (newVal >= 100 && !this.sectionIsComplete) {
        this.sectionIsComplete = true;
        this.checkSectionComplete();
      } else if (newVal < 100 && this.sectionIsComplete) {
        // If somehow completion drops, reset sectionIsComplete (unlikely but good for robustness)
        this.sectionIsComplete = false;
      }
    },
  },
  methods: {
    // Renamed from updateSession to be more descriptive of what it does locally
    handleTrackCurrentTime(actionSet) {
      // Find the interaction and update it locally.
      // Make sure actionSet contains enough info (id, timestamp)
      const index = this.sessionInteractions.findIndex(interaction => interaction && interaction.id === actionSet.id);
      if (index !== -1) { // Check for -1 instead of truthiness (0 is a valid index)
        // Vue's reactivity for arrays: Use Vue.set or array methods for reactive updates
        // For object properties, direct mutation is okay if the object itself is reactive.
        this.$set(this.sessionInteractions, index, {
          ...this.sessionInteractions[index],
          ...actionSet // Merge new actionSet data
        });
      } else {
        // If interaction not found, add it (e.g., first interaction for this session)
        this.sessionInteractions.push(actionSet);
      }
      // The computed properties (timeCompleted, totalTime, percentViewed) will reactively update.
      // No need to call updateCurrentStatus manually here anymore.
    },

    // Renamed handler for @updateCourse emit
    handleUpdateCourse(actionSet) {
      // This actionSet likely contains dateCompleted.
      // Update local interactions first.
      this.handleTrackCurrentTime(actionSet); // Re-use the update logic
      
      // Now, specifically handle completion status
      if (actionSet && actionSet.dateCompleted) {
        const sessionToMarkComplete = this.processedSessions.find(s => s.slug === actionSet.slug);
        if (sessionToMarkComplete) {
          this.addToCompletedSessions(sessionToMarkComplete); // Use the already processed session data
        }
      }
    },

    // This method now only focuses on checking and emitting section completion
    updateSectionCompletionStatus() {
      // Re-evaluate sessionsCompleted based on current processedSessions state
      this.sessionsCompleted = this.processedSessions.filter(session => session.dateCompleted);
      
      if (this.sessionsCompleted.length === this.processedSessions.length && this.processedSessions.length > 0) {
        this.sectionIsComplete = true;
        this.checkSectionComplete();
      } else {
        this.sectionIsComplete = false; // Reset if not all are complete
      }
    },

    // This method is primarily for emitting the course section completion
    checkSectionComplete() {
      const { sessionsCompleted, sectionKey } = this
      // Only emit if section is truly complete and there's data
      if (this.sectionIsComplete && sessionsCompleted.length > 0) {
        this.$emit('courseSectionCompleted', {
          sectionKey,
          sectionCompleted: sessionsCompleted[0].dateCompleted, // Or some other relevant completion date
        })
      }
      // No need for this.$forceUpdate() anymore, reactivity handles it.
    },

    // Previous `updateCurrentStatus` logic is now handled by computed properties:
    // `totalTime`, `timeCompleted`, `percentViewed`.
    // The side effect of checking section complete is handled in the `percentViewed` watcher.

    // `getGroupPercentViewed` is also replaced by the `percentViewed` computed property.

    putInteraction(obj) {
      return this.$store.dispatch('user/putInteraction', obj)
    },

    getCurrentSession($f) {
      const { $route, processedSessions, getNextSession } = this // Use processedSessions
      const session = processedSessions.find(s => $route.hash.replace('#', '') === s.slug)
      this.currentSession = typeof session != 'undefined' ? session : false
      if (this.currentSession) {
        getNextSession();
        this.hideSessions = false
      } else {
        this.hideSessions = true
      }
      this.implementScroll();
      return this.currentSession
    },
    findCurrentSession($f) {
      const { $route, processedSessions, getNextSession } = this // Use processedSessions
      const currentSession = processedSessions.find(s => !s.dateCompleted) // Simpler check
      this.currentSession = currentSession || false
      if ($route.hash === '' && checkNested(this.currentSession, 'slug')) {
        this.$router.push({
          path: $route.path,
          hash: this.currentSession.slug
        })
      }
    },
    getNextSession() {
      const { processedSessions, currentSession, $route, getNextSessionFromNextSection } = this // Use processedSessions
      const nextSession = processedSessions.find(s => !s.dateCompleted && s.slug !== currentSession.slug)
      this.nextSession = checkNested(nextSession, 'slug') ? nextSession : getNextSessionFromNextSection()
      console.log(this.nextSession)
      return this.nextSession
    },
    getNextSessionFromNextSection() {
      // This logic seems fine, using store getters directly.
      if (this.nextSection) { // Simplified `_isUndefined` check
        const nextChildrenSection = cloneDeep(this.$store.getters['posts/filterAllSessionsByProp'](this.nextSection.children))
        const nextSession = nextChildrenSection.find(s => !s.dateCompleted && s.slug !== this.$route.hash.replace('#', ''))
        return checkNested(nextSession, 'slug') ? nextSession : {title: '', slug: ''}
      } else {
        return {title: '', slug: ''}
      }
    },
    // addToCompletedSessions is still relevant to manage the `sessionsCompleted` array
    addToCompletedSessions(obj) {
      // Ensure the object is not already in the list
      if (!this.sessionsCompleted.some(s => obj.slug === s.slug)) {
        this.sessionsCompleted.push(obj);
        this.sessionsCompleted.sort((a, b) => new Date(b.dateCompleted).getTime() - new Date(a.dateCompleted).getTime());
      }
      // The `percentViewed` watcher and `updateSectionCompletionStatus` will now react to this change.
      // Removed the direct call to checkSectionComplete here as the watcher on `percentViewed` handles it.
    },

    getSessionElements($check) {
      const {$route, findCurrentSession, getCurrentSession} = this
      // checking route keeps find current from being triggered when using back button
      if ($route.hash === '' && $check !== 'route') { // Use === for strict comparison
        findCurrentSession({currentFind: $check})
      } else {
        getCurrentSession({currentGet: $check})
      }
    },
    toggleSection() {
      this.hideSessions = !this.hideSessions
      // When opening, navigate to first session with media so its player activates
      if (!this.hideSessions && this.$route.hash === '') {
        const firstWithMedia = this.processedSessions.find(s =>
          s.session_type && (s.session_type[0] === 'video' || s.session_type[0] === 'audio')
        )
        if (firstWithMedia && firstWithMedia.slug) {
          this.$router.push({ path: this.$route.path, hash: firstWithMedia.slug })
        }
      }
    },
    implementScroll() {
      const { currentSession } = this
      setTimeout(() => {
        const currentSlug = currentSession.slug
        // Use document.getElementById for robust element lookup by ID
        const element = document.getElementById(currentSlug);
        if (element) {
          element.scrollIntoView({behavior: "smooth", block: "start", inline: "start"});
        }
      }, 500)
    }
  },

  mounted() {
    // Initial calls are now handled by `immediate: true` on relevant watchers
  },
  beforeMount() {
    // Populate sessionInteractions from store
    this.sessionInteractions = this.$store.getters['user/findInteractionsByProp'](this.children) || [];
    // The rest of the setup is handled by `processedSessions` computed property and its watchers.
  },
  components: {
    PostListItem,
    Loader,
  },
  filters: {
    ...secondConversions,
  },
}
</script>
<style scoped>
/* Your styles remain the same */
h2 {
  margin-bottom: 0;
}
.session-list {
  margin-bottom: 2rem;
  &:last-of-type {
    margin-bottom: 0;
  }
  &__overview {
    display: grid;
    grid-template-columns: 1fr;
    width: 100%;
    /* border: 1px solid var(--gray); */
    color: var(--lightGray);
    border-radius: var(--borderRadiusSmall) var(--borderRadiusSmall) 0 0;
    background-color: var(--brandPrimary);
    padding: 1rem 2rem;
    transition: all 500ms ease-in-out;
    cursor: pointer;
    align-items: center;
    min-height: 96px;
    @media(--tablet) {
      grid-template-columns: 1fr auto;
      gap: 1rem;
    }
    &:hover {
      background-color: var(--brandAccent);
    }
    /* @media (--tablet-landscape) {
      grid-template-columns: auto auto 1fr;
    } */
    &__success {
      /* background-color: var(--colorSuccess); */
      color: white;
    }
    &__closed {
      border-radius: var(--borderRadiusSmall);
    }
    &__item {
      display: flex;
      align-items: center;
      padding: 0.5rem 2rem;
      border-bottom: 1px solid var(--mediumGray); /* Changed from --gray for consistency, assuming mediumGray is intended for borders */
      @media (--tablet-landscape) {
        border: none;
        border-right: 1px solid var(--mediumGray); /* Changed from --gray */
      }
      &:last-child {
        border: none;
      }
      &Stat {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        line-height: 1;
        &__stat {
          font-weight: var(--fontWeightLight);
          font-size: 2.5rem;
        }
        &__label {
          font-size: 0.7rem;
          font-weight: var(--fontWeightBold);
          letter-spacing: 0.05rem;
          text-transform: uppercase;
        }
        &__progress {
          background-color: var(--mediumGray);
          height: 10px;
          width: 100%;
          position: relative;
          border-radius: 5px;
          overflow: hidden;
          &__status {
            display: block;
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 0;
            background-color: var(--gray);
            will-change: width;
          }
        }
      }
      &--progress {
        flex: 1;
        justify-content: flex-end;
        .session-list__overview__itemStat {
          align-items: flex-start;
          /* transform: translateY(-8px); */
          &__label {
            margin-bottom: 3px;
          }
        }
      }
    }
  }
  &__items {
    margin: 0.25rem 0 0 0;
    &__item {
      padding: 1rem 0;
      border-bottom: 1px solid var(--mediumGray);
      &:last-of-type {
        margin-bottom: 0;
        overflow: hidden;
        border-radius: 0 0 var(--borderRadiusSmall) var(--borderRadiusSmall);
        border-bottom: none;
      }
    }
  }
}
.percentage-indicator {
  font-weight: 700;
}
.button__icon {
  font-size: 2rem;
}
/* .separator {
  margin-top: 2rem;
  width: 100%;
  height: 2px;
  background-color: var(--mediumGray);
} */
/* Enter and leave animations can use different */
/* durations and timing functions.              */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter, .slide-fade-leave-to
/* .slide-fade-leave-active below version 2.1.8 */ {
  transform: translateY(-100px);
  opacity: 0;
}
</style>